import { useEffect, useRef, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "./context/AuthContext";
import Login from "./pages/Login/Login";
import AdminLogin from "./admin/pages/AdminLogin/AdminLogin";
import UserApp from "./UserApp";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
const AdminApp = lazy(() => import("./AdminApp"));

// Shared by the auth check and the admin chunk download, so a slow admin load
// shows the same spinner as everything else instead of a bare unstyled line
function FullScreenLoader({ label }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#0A0A0F',
      color: '#FFFFFF',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div className="spinner" style={{
          width: '3rem',
          height: '3rem',
          border: '3px solid rgba(0, 255, 255, 0.3)',
          borderTopColor: '#00FFFF',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          margin: '0 auto 1rem',
        }}></div>
        <p>{label}</p>
      </div>
    </div>
  );
}

const AdminLoading = () => <FullScreenLoader label="Loading admin..." />;

export default function AuthGate() {
  const { role, loading } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const previousRoleRef = useRef(role);

  // Normalize pathname to lowercase for case-insensitive matching
  const normalizedPath = location.pathname.toLowerCase();

  // Redirect /Admin/login to /admin/login for consistency
  useEffect(() => {
    if (location.pathname.toLowerCase() === "/admin/login" && location.pathname !== "/admin/login") {
      navigate("/admin/login", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Handle navigation after role changes or when already authenticated on login pages
  useEffect(() => {
    const isLoginPage = normalizedPath === "/signin" || normalizedPath === "/login";
    const isAdminLoginPage = normalizedPath === "/admin/login";

    if (role === "admin" && isAdminLoginPage) {
      navigate("/admin/dashboard", { replace: true });
    } else if ((role === "user" || role === "consumer") && isLoginPage) {
      navigate("/", { replace: true });
    }
    
    previousRoleRef.current = role;
  }, [role, normalizedPath, navigate]);

  // Protect admin routes
  useEffect(() => {
    // Don't redirect while loading auth state
    if (loading) return;
    
    const isAdminRoute = normalizedPath.startsWith("/admin");
    const isAdminLoginRoute = normalizedPath === "/admin/login";
    
    if (isAdminRoute && !isAdminLoginRoute && role !== "admin") {
      // Non-admin trying to access admin routes (except login)
      navigate("/", { replace: true });
    }
  }, [normalizedPath, role, navigate, loading]);

  // Show loading while checking auth
  if (loading) {
    return <FullScreenLoader label="Loading..." />;
  }

  // Admin login page - case-insensitive
  if (normalizedPath === "/admin/login") {
    return <AdminLogin />;
  }

  // User login page
  if (location.pathname === "/signin" || location.pathname === "/login") {
    return <Login />;
  }

  // Signup page
  if (location.pathname === "/signup" || location.pathname === "/register") {
    return <Login />;
  }

  const isAdminRoute = normalizedPath.startsWith("/admin");

  // Admin routes: only show when role is admin
  if (isAdminRoute) {
    if (role !== "admin") {
      // The redirect effect above is about to move us off /admin. Render the
      // loader rather than null so a stalled redirect shows a spinner instead
      // of an unexplained blank white page.
      return <FullScreenLoader label="Loading..." />;
    }
    return (
      <ErrorBoundary variant="dark" title="The admin panel failed to load">
        <Suspense fallback={<AdminLoading />}>
          <AdminApp />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // All non-admin routes: always show customer app (UserApp)
  return <UserApp />;
}

